import PageLayout from 'components/pages/PageLayout'
import React from 'react'
import { GetStaticPathsResult, NextPage } from 'next'
import { withApollo } from 'components/withApollo'
import PageMeta from 'components/PageMeta'
import { useRouter } from 'next/router'
import { useQuery } from '@apollo/client'
import getStaticApolloProps from 'lib/getStaticApolloProps'
import createApolloClient from 'components/withApollo/createApolloClient'
import NotFoundPage from 'components/pages/NotFoundPage'
import ErrorPage from 'components/pages/ErrorPage'
import FeatureSection from 'components/pages/CateringMarketPage/FeatureSection'
import CtaSection from 'components/pages/CateringMarketPage/CtaSection'
import DescriptionSection from 'components/pages/CateringMarketPage/DescriptionSection'
import Events from 'components/pages/CateringMarketPage/Events'
import StatsSection from 'components/StatsSection'
import CateringRatings from 'components/CateringRatings'
import FoodTypesSection from 'components/FoodTypesSection'

import MARKET_PATHS_QUERY from './marketPathsQuery'
import { marketCateringPathsQuery } from './types/marketCateringPathsQuery'
import {
  marketCateringQuery,
  marketCateringQueryVariables
} from './types/marketCateringQuery'
import MARKET_QUERY from './query'
import HeroSection from './CateringMarketHeroSection'

type Props = {}

type Params = {
  market: string
}

const CateringMarketPage: NextPage = () => {
  const router = useRouter()

  const { market: slug } = router.query as Params

  const { data, error } = useQuery<
    marketCateringQuery,
    marketCateringQueryVariables
  >(MARKET_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      slug
    }
  })

  if (error) return <ErrorPage message={error.message} />
  if (!data?.marketBySlug) {
    return <NotFoundPage search={slug} />
  }

  const market = data.marketBySlug

  return (
    <PageLayout fullWidth staticPage>
      <PageMeta
        canonicalPath={market.customerCateringPath}
        coordinates={[market.latitude, market.longitude]}
        image={market.image.medium}
        keywords={`food trucks, map, ${market.name}, ${market.stateFull}, catering`}
        ldJson={data.structuredData.marketCateringPage}
        title={`Best Food Trucks | Catering in ${market.name}`}
      />
      <div className="w-full">
        <HeroSection market={market} state={market.state} isCateringPage />

        <StatsSection
          description="By managing food truck daily life from event booking, to online ordering and catering we have cultivated an intimate relationship with great vendors throughout the country and know exactly which truck(s) will suit your needs, from a small neighborhood party to massive corporate events."
          points={[
            {
              label: 'Caterings',
              value: '2,000+'
            },
            {
              label: 'Attendees',
              value: '300,000+'
            },
            {
              label: 'Cuisines',
              value: '80+'
            }
          ]}
          title={`Best Catering Expertise in ${market.name}`}
        />
        <FoodTypesSection foodTypePages={data.foodTypePages} />
        <CtaSection className="lg:mt-24 mt-16" marketName={market.name} />
        <DescriptionSection
          cateringDescription1={market.cateringDescription1}
          cateringDescription2={market.cateringDescription2}
        />
        <FeatureSection />
        <CateringRatings className="mt-10" marketId={market.id} />
        <Events categories={data.cateringCategories} />
        {data.cateringCategories.filter(
          (c) => !c.mainPage && !c.parentCategory && c.active
        ).length > 0 && <CtaSection marketName={market.name} />}
      </div>
    </PageLayout>
  )
}

export default withApollo({ ssr: false })(CateringMarketPage as NextPage)

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  const client = createApolloClient({}, null)

  const { data } = await client.query<marketCateringPathsQuery>({
    query: MARKET_PATHS_QUERY
  })

  const paths = data.customerPaths.marketPaths.map((marketPath) => ({
    params: { market: marketPath }
  }))

  return {
    paths,
    fallback: 'blocking'
  }
}

export const getStaticProps = getStaticApolloProps<Props, Params>(
  CateringMarketPage,
  {
    revalidate: 60 * 30 // 30 minutes
  }
)
